/**
 * Small SVG charts. Every mark carries a printed label or a legend with values,
 * so colour is never the only way to read one (the owner is colourblind, and
 * three light-mode series colours sit below 3:1 contrast). Hovering a month
 * shows a tooltip with the breakdown behind it; hit areas are whole columns,
 * not the thin marks, and work with a tap on a phone.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { GROUP_LABEL, type Amount, type Group, type MonthFlow, type NetWorthPoint } from './lib/analysis'

const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const compact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 })
const pct = (n: number) => `${(n * 100).toFixed(n < 0.1 ? 1 : 0)}%`

/** Asset classes keep one colour wherever they appear: colour follows the entity, never its rank. */
export const CLASS_ORDER = ['us_stock', 'intl_stock', 'bond', 'cash', 'real_estate', 'target_date', 'commodity', 'crypto', 'other']
export const CLASS_LABEL: Record<string, string> = {
  us_stock: 'US stocks',
  intl_stock: 'International stocks',
  bond: 'Bonds',
  cash: 'Cash & money market',
  real_estate: 'Real estate',
  target_date: 'Target-date funds',
  commodity: 'Commodities',
  crypto: 'Crypto',
  other: 'Other',
}
const seriesVar = (cls: string) => {
  const i = CLASS_ORDER.indexOf(cls)
  return i < 0 || cls === 'other' ? 'var(--series-other)' : `var(--series-${i + 1})`
}

/** Rounds a maximum up to a tidy axis top: 1, 2, 2.5 or 5 × 10ⁿ. */
export function niceMax(v: number): number {
  if (v <= 0) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  return ([1, 2, 2.5, 5, 10].find((m) => m * p >= v) ?? 10) * p
}

/**
 * Months read YYYY-MM like every other date ("Oct 26" reads as the 26th).
 * Past eight months about eight are labelled, counting back from the latest.
 */
export const monthLabel = (m: string, i: number, count: number) => (count <= 8 || (count - 1 - i) % Math.ceil(count / 8) === 0 ? m : '')

/**
 * Which column the pointer is over, forgotten when the data no longer has it:
 * a hover left on month 11 of 13 must not survive a switch to six months.
 */
export function useHover(count: number) {
  const [hover, setHover] = useState<number>()
  return [hover !== undefined && hover < count ? hover : undefined, setHover] as const
}

/** A tooltip over an SVG chart, placed by the hovered column's centre as a share of the width. */
export function Tip({ at, top, children }: { at: number; top?: number; children: ReactNode }) {
  // Beside the hovered column, on the side with more room, so it never covers what it describes.
  const edge = at < 0.5 ? 'left' : 'right'
  return (
    <div className={`tip tip-${edge}`} style={{ left: `${at * 100}%`, top: top ?? 0 }} role="status">
      {children}
    </div>
  )
}

/**
 * The width to lay a chart out at: its column's width in CSS pixels, so text
 * stays at the page's size however wide the column is, but never under 640.
 * A narrower column scales the 640 drawing down, as before, so phone-width
 * labels shrink rather than collide.
 */
export function useWidth(min = 640) {
  const ref = useRef<HTMLElement>(null)
  const [width, setWidth] = useState(min)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setWidth(Math.max(min, Math.round(el.clientWidth)))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  })
  return [ref, width] as const
}

const GROUP_ORDER: Group[] = ['cash', 'taxable', 'retirement', 'hsa', 'education', 'property', 'other', 'debt']

export function NetWorthChart({ points }: { points: NetWorthPoint[] }) {
  const [hover, setHover] = useHover(points.length)
  const [ref, W] = useWidth()
  if (points.length < 2) {
    return <p className="muted">The line starts once two months of statements from every account are in.</p>
  }
  const H = 220
  const pad = { l: 64, r: 16, t: 16, b: 28 }
  const values = points.map((p) => p.net)
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const span = hi - lo || Math.abs(hi) * 0.1 || 1
  const min = lo - span * 0.25
  const max = hi + span * 0.25
  const x = (i: number) => pad.l + (i * (W - pad.l - pad.r)) / (points.length - 1)
  const y = (v: number) => pad.t + ((max - v) / (max - min)) * (H - pad.t - pad.b)
  const ticks = [0, 0.5, 1].map((f) => min + f * (max - min))
  // Enough decimals that the three axis labels differ ($1.73M, $1.74M, not $1.7M twice).
  const tickFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: (max - min) / Math.max(Math.abs(max), 1) < 0.1 ? 2 : 1,
  })
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.net)}`).join(' ')
  const area = `${line} L${x(points.length - 1)},${H - pad.b} L${x(0)},${H - pad.b} Z`
  const last = points.at(-1)!
  const change = last.net - points[0].net
  return (
    <figure className="chart hoverable" ref={ref}>
      <figcaption className="muted">
        {whole.format(last.net)} at {last.month}, {change >= 0 ? 'up' : 'down'} {whole.format(Math.abs(change))} since {points[0].month}
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Net worth by month">
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid" x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} />
            <text className="tick" x={pad.l - 8} y={y(t) + 4} textAnchor="end">{tickFormat.format(t)}</text>
          </g>
        ))}
        <path className="area" d={area} />
        <path className="line" d={line} />
        {hover !== undefined && <line className="crosshair" x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} />}
        {points.map((p, i) => (
          <g key={p.month}>
            <circle className="dot" cx={x(i)} cy={y(p.net)} r={hover === i ? 6 : 4} />
            <text className="tick" x={x(i)} y={H - 8} textAnchor="middle">{monthLabel(p.month, i, points.length)}</text>
          </g>
        ))}
        <text className="value" x={x(points.length - 1)} y={y(last.net) - 10} textAnchor="end">{tickFormat.format(last.net)}</text>
        {points.map((p, i) => {
          const half = (W - pad.l - pad.r) / (points.length - 1) / 2
          return (
            <rect
              key={p.month}
              className="hit"
              x={Math.max(pad.l, x(i) - half)}
              width={Math.min(x(i) + half, W - pad.r) - Math.max(pad.l, x(i) - half)}
              y={pad.t}
              height={H - pad.t - pad.b}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(undefined)}
              onClick={() => setHover(hover === i ? undefined : i)}
            />
          )
        })}
      </svg>
      {hover !== undefined && (
        <Tip at={x(hover) / W}>
          <b className="num">{points[hover].month}</b>
          <table>
            <tbody>
              <tr><td>Net worth</td><td className="num r">{whole.format(points[hover].net)}</td></tr>
              {GROUP_ORDER.filter((g) => points[hover].by_group?.[g]).map((g) => (
                <tr key={g} className="sub"><td>{g === 'debt' ? 'Owed' : GROUP_LABEL[g]}</td><td className="num r">{g === 'debt' ? '−' : ''}{whole.format(points[hover].by_group[g]!)}</td></tr>
              ))}
              {hover > 0 && (
                <tr>
                  <td>Change on {points[hover - 1].month}</td>
                  <td className="num r">{points[hover].net >= points[hover - 1].net ? '▲ ' : '▼ '}{whole.format(Math.abs(points[hover].net - points[hover - 1].net))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Tip>
      )}
    </figure>
  )
}

/** Income above the line, spending below it: position says which is which before colour does. */
export function CashFlowChart({
  months,
  details,
  onSelect,
}: {
  months: MonthFlow[]
  /** The largest spending items of a month, for the tooltip. */
  details?: (month: string) => { merchant: string; amount: number }[]
  onSelect?: (month: string) => void
}) {
  const [hover, setHover] = useHover(months.length)
  const [ref, W] = useWidth()
  if (months.length === 0) return <p className="muted">No checking, savings or card activity yet.</p>
  const H = 240
  const pad = { l: 56, r: 8, t: 20, b: 40 }
  const top = niceMax(Math.max(...months.map((m) => m.income), 1))
  const bottom = niceMax(Math.max(...months.map((m) => m.spending), 1))
  const plotH = H - pad.t - pad.b
  const zero = pad.t + (top / (top + bottom)) * plotH
  const scale = plotH / (top + bottom)
  const slot = (W - pad.l - pad.r) / months.length
  const bar = Math.min(48, slot * 0.5)
  return (
    <figure className="chart hoverable" ref={ref}>
      <div className="legend">
        <span><i className="swatch" style={{ background: 'var(--series-1)' }} /> Income (up)</span>
        <span><i className="swatch" style={{ background: 'var(--series-2)' }} /> Spending (down)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Income and spending by month">
        <text className="tick" x={pad.l - 8} y={pad.t + 4} textAnchor="end">{compact.format(top)}</text>
        <text className="tick" x={pad.l - 8} y={zero + 4} textAnchor="end">$0</text>
        <text className="tick" x={pad.l - 8} y={H - pad.b + 4} textAnchor="end">−{compact.format(bottom)}</text>
        <line className="axis" x1={pad.l} x2={W - pad.r} y1={zero} y2={zero} />
        {months.map((m, i) => {
          const cx = pad.l + slot * i + slot / 2
          const up = m.income * scale
          const down = m.spending * scale
          return (
            <g key={m.month}>
              {hover === i && <rect className="column" x={pad.l + slot * i + 2} width={slot - 4} y={pad.t - 14} height={H - pad.t - pad.b + 14} rx={4} />}
              <rect className="bar" x={cx - bar / 2} y={zero - up} width={bar} height={Math.max(up, 0.5)} rx={2} style={{ fill: 'var(--series-1)' }} />
              <rect className="bar" x={cx - bar / 2} y={zero + 1} width={bar} height={Math.max(down, 0.5)} rx={2} style={{ fill: 'var(--series-2)' }} />
              {/* Too narrow for every figure, only the labelled months carry them; the hover has the rest. */}
              {m.income > 0 && (slot >= 52 || monthLabel(m.month, i, months.length)) && (
                <text className="value" x={cx} y={zero - up - 5} textAnchor="middle">{compact.format(m.income)}</text>
              )}
              <text className="tick" x={cx} y={H - 22} textAnchor="middle">{monthLabel(m.month, i, months.length)}</text>
              {(slot >= 52 || monthLabel(m.month, i, months.length)) && (
                <text className={m.net < 0 ? 'value neg' : 'value'} x={cx} y={H - 6} textAnchor="middle">
                  {m.net < 0 ? '−' : '+'}{compact.format(Math.abs(m.net))}
                </text>
              )}
              <rect
                className={onSelect ? 'hit clickable' : 'hit'}
                x={pad.l + slot * i}
                width={slot}
                y={0}
                height={H}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(undefined)}
                onClick={() => onSelect?.(m.month)}
              >
                {onSelect && <title>{`Show ${m.month} transactions`}</title>}
              </rect>
            </g>
          )
        })}
      </svg>
      {hover !== undefined && (
        <Tip at={(pad.l + slot * hover + slot / 2) / W}>
          <b className="num">{months[hover].month}</b>
          <table>
            <tbody>
              <tr><td>+ Income</td><td className="num r">{whole.format(months[hover].income)}</td></tr>
              <tr><td>− Spending</td><td className="num r">{whole.format(months[hover].spending)}</td></tr>
              <tr><td>= Net</td><td className="num r">{months[hover].net < 0 ? '−' : ''}{whole.format(Math.abs(months[hover].net))}</td></tr>
              {details?.(months[hover].month).map((d, j) => (
                <tr key={j} className="sub"><td>{d.merchant}</td><td className="num r">{whole.format(d.amount)}</td></tr>
              ))}
            </tbody>
          </table>
          {onSelect && <p className="tip-hint">Click for every transaction</p>}
        </Tip>
      )}
      <p className="label">Bottom row: income minus spending for the month</p>
    </figure>
  )
}

/** One 100% bar, segments in fixed class order, with a legend that prints every value. */
export function AllocationChart({ allocation, total }: { allocation: (Amount & { share: number })[]; total: number }) {
  if (allocation.length === 0) return <p className="muted">No holdings listed on the statements yet.</p>
  const ordered = [...allocation].sort((a, b) => CLASS_ORDER.indexOf(a.label) - CLASS_ORDER.indexOf(b.label))
  return (
    <figure className="chart">
      <div className="stack" role="img" aria-label="Asset allocation">
        {ordered.map((a) => (
          <span key={a.label} style={{ width: `${a.share * 100}%`, background: seriesVar(a.label) }} title={`${CLASS_LABEL[a.label] ?? a.label}: ${whole.format(a.total)}, ${pct(a.share)}`} />
        ))}
      </div>
      <table className="table legend-table">
        <tbody>
          {ordered.map((a) => (
            <tr key={a.label}>
              <td><i className="swatch" style={{ background: seriesVar(a.label) }} /> {CLASS_LABEL[a.label] ?? a.label}</td>
              <td className="r num">{pct(a.share)}</td>
              <td className="r num">{whole.format(a.total)}</td>
            </tr>
          ))}
          <tr className="total">
            <td>Total in listed holdings</td>
            <td />
            <td className="r num">{whole.format(total)}</td>
          </tr>
        </tbody>
      </table>
    </figure>
  )
}

/** One-series horizontal bars; the label and amount are always printed, so colour carries nothing. */
export function Bars({
  items,
  total,
  format = (n) => whole.format(n),
  name = (s) => s,
  note,
  onSelect,
  color,
}: {
  items: Amount[]
  total?: number
  format?: (n: number) => string
  name?: (s: string) => string
  note?: (a: Amount) => string
  /** Makes each label a button that opens the transactions behind it. */
  onSelect?: (label: string) => void
  /** Bar colour: a series token by role (money held or earned, money spent or owed); grey when unset. */
  color?: string
}) {
  if (items.length === 0) return <p className="muted">Nothing here yet.</p>
  const max = Math.max(...items.map((i) => i.total))
  return (
    <ul className="bars">
      {items.map((i) => {
        const share = total ? ` · ${pct(i.total / total)}` : ''
        return (
          <li key={i.label} title={`${name(i.label)}: ${format(i.total)}${note ? `, ${note(i)}` : ''}${share}`}>
            {onSelect ? (
              <button className="link bar-label" onClick={() => onSelect(i.label)}>{name(i.label)}</button>
            ) : (
              <span className="bar-label">{name(i.label)}</span>
            )}
            <span className="bar-value num">{format(i.total)}<span className="share">{share}</span></span>
            <span className="bar-track"><span className="bar-fill" style={{ width: `${(i.total / max) * 100}%`, ...(color ? { background: color } : {}) }} /></span>
          </li>
        )
      })}
    </ul>
  )
}
